import { useAuiState } from '@assistant-ui/react';

import { useWorkbenchRunStatus } from './workbench-run-status-context.js';
import { formatElapsed, useElapsedSeconds } from './use-elapsed-seconds.js';

/**
 * Indicator shown while the thread is running. Displays the active tool name when
 * a tool call is in progress, and always how long the run has been going — the
 * signal that a quiet workbench is still alive. Between tool calls the elapsed reading
 * stands alone, rather than pairing with a second animation.
 */
export function ThinkingIndicator() {
	// Read from the run, not from this component's mount: the indicator only appears
	// once the thread reports running, which for a resumed run is after the history
	// load and stream reconnect.
	const runStartedAt = useWorkbenchRunStatus((state) => state.runStartedAt);
	const elapsedSeconds = useElapsedSeconds(runStartedAt);
	const runningToolName = useAuiState((s) => {
		if (!s.thread.isRunning) {
			return null;
		}
		const lastMsg = s.thread.messages.at(-1);
		if (lastMsg?.role !== 'assistant') {
			return null;
		}
		for (const p of lastMsg.parts) {
			if (p.type === 'tool-call' && p.status.type === 'running') {
				return p.toolName;
			}
		}
		return '';
	});

	if (runningToolName === null) {
		return null;
	}

	return (
		<div className='mb-4 grid grid-cols-[46px_1fr] items-center gap-x-2.5'>
			<div className='flex justify-end select-none'>
				<img src='/images/Cadstrom_DARK_PURPLE_LOGOMARK.png' alt='' className='h-4 w-4 object-contain' />
			</div>
			<div className='flex items-center gap-2'>
				{runningToolName && <span className='text-sm text-gray-a9'>{`calling ${runningToolName}…`}</span>}
				<span className='font-mono text-xs text-gray-a8 tabular-nums' aria-label='Time elapsed'>
					{formatElapsed(elapsedSeconds)}
				</span>
			</div>
		</div>
	);
}
